import { useCallback, useState, useMemo } from 'react'
import { getWritingStyle } from '../AIStylesService'

// Style ID constants
const HARDCODED_DEFAULT_STYLE_ID = '0'

export interface WritingStyle {
    id: string
    title: string
    description?: string
    generatedStyle?: string
    name?: string
    attributes?: {
        name: string
        description?: string
        generated_style?: string
        preferred?: boolean
        writing_style_status?: string
    }
}

export interface WritingStylesHookReturn {
    writingStyles: WritingStyle[]
    isLoading: boolean
    setIsLoading: React.Dispatch<React.SetStateAction<boolean>>
    defaultStyle: WritingStyle
    fetchWritingStyles: () => Promise<void>
    setWritingStyles: React.Dispatch<React.SetStateAction<WritingStyle[]>>
}

export const useWritingStyles = (): WritingStylesHookReturn => {
    const defaultStyle: WritingStyle = useMemo(
        () => ({
            id: HARDCODED_DEFAULT_STYLE_ID,
            title: 'DILIGENT_AI_STYLE',
            description: 'DILIGENT_AI_STYLE_DESCRIPTION',
            generatedStyle: '',
            attributes: {
                name: 'DILIGENT_AI_STYLE',
                preferred: true
            }
        }),
        []
    )

    const [writingStyles, setWritingStyles] = useState<WritingStyle[]>([])
    const [isLoading, setIsLoading] = useState(false)

    const mapApiResponseToWritingStyle = (item: any): WritingStyle => ({
        id: item?.id,
        title: item?.attributes?.name,
        description: item?.attributes?.description || '',
        generatedStyle: item?.attributes?.generated_style || '',
        attributes: {
            name: item?.attributes?.name,
            description: item?.attributes?.description,
            generated_style: item?.attributes?.generated_style,
            preferred: item?.attributes?.preferred || false,
            writing_style_status: item?.attributes?.writing_style_status
        }
    })

    const fetchWritingStyles = useCallback(async () => {
        try {
            setIsLoading(true)

            const response = await getWritingStyle()

            if (response && response?.data) {
                const responseData = Array.isArray(response.data) ? response.data : [response.data]
                const mappedStyles = responseData.map(mapApiResponseToWritingStyle)

                const firstCustomStyle = mappedStyles[0]
                const isFirstCustomPreferred = firstCustomStyle?.attributes?.preferred || false

                const updatedDefaultStyle = {
                    ...defaultStyle,
                    attributes: {
                        ...defaultStyle.attributes,
                        preferred: !isFirstCustomPreferred
                    }
                }

                const allStyles = [updatedDefaultStyle, ...mappedStyles]
                setWritingStyles(allStyles)
            } else {
                setWritingStyles([defaultStyle])
            }
        } catch (error) {
            setWritingStyles([defaultStyle])
            throw error
        } finally {
            setIsLoading(false)
        }
    }, [defaultStyle])

    return {
        writingStyles,
        isLoading,
        defaultStyle,
        fetchWritingStyles,
        setWritingStyles,
        setIsLoading
    }
}
